import { userIntegrations } from '../constants/integrations';
import { SIGNATURE } from '../constants/signature';
import {
  BUDGET_CURRENCY,
  CRM_SKIP_ALLOWED,
  DIGITAL_BALANCE_EMAILS_DISABLED,
  DISPLAY_CURRENCY,
  DISTRIBUTOR_ORG_IDS,
  SKIP_WELCOME_EMAIL,
} from '../constants/users';
import { IErrorResponse } from './bucket';
import { IPromiseCallbacks } from './redux';
import { ViewTypesEnum } from './shell';

export enum UserStatusEnum {
  Active = 'active',
  NotActive = 'not_active',
}

export enum UserIntegrationStatusesEnum {
  ON = 'ON',
  OFF = 'OFF',
  NA = 'NA',
}

export type TUserIntegrations = {
  [K in (typeof userIntegrations)[number]]: UserIntegrationStatusesEnum;
};

export interface IUser {
  address1: string;
  address2: string;
  city: string;
  country: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  state: string;
  [SIGNATURE]: string;
  zip: string;
  department_id: string;
  [DISTRIBUTOR_ORG_IDS]: string[];
  org_id: string;
  admin_type: string;
  available_budget: number;
  used_budget: number;
  picture: string;
  integration: TUserIntegrations;
  uid: string;
  [CRM_SKIP_ALLOWED]: boolean;
  // Add-user flow only: opt out of the one-time welcome email. Not present on edit/fetched users.
  [SKIP_WELCOME_EMAIL]?: boolean;
  [DIGITAL_BALANCE_EMAILS_DISABLED]?: boolean;
  [DISPLAY_CURRENCY]: string;
  [BUDGET_CURRENCY]: string;
  last_sign_in_time: string;
  status?: UserStatusEnum;
}

export interface IUsersState {
  items: IUser[] | null;
  userDetails: Partial<IUser> | null;
  isLoading: boolean;
  error: IErrorResponse | null;
  visibleUsersColumns: Record<string, boolean>;
  activeView: ViewTypesEnum;
}

export interface IAddUserActionPayload extends IPromiseCallbacks {
  user: Omit<IUser, 'integration' & 'uid' & 'org_id'>;
}

export interface IEditUserActionPayload extends IPromiseCallbacks {
  user: Partial<IUser>;
}

export interface IDeleteUserActionPayload extends IPromiseCallbacks {
  userId: string;
  newOwnerId?: string | undefined;
}

export interface IToggleDigitalBalanceEmailsActionPayload {
  [DIGITAL_BALANCE_EMAILS_DISABLED]: boolean;
}

export interface IResponseUser {
  integration: TUserIntegrations;
  user: IUser;
}

export interface IFetchUsersResponse {
  users: IResponseUser[];
}

export interface IEditUserProfileRequestPayload {
  [key: string]: string;
}

/**
 * Metadata for editing user profile
 * @param isSilent - if true, the request will not show a global loading spinner,
 * in cases when user should be changed from inside the table and we want to hide the global spinner
 */

export interface IFetchUserProfileRequestMetadata {
  isSilent?: boolean;
}
